import { Banknote, Building2, Wallet } from "lucide-react";

type AccountingSummaryCardProps = {
  cash: number;
  bank: number;
  openingBalance: number;
  onConfirm: () => void;
};

const formatAmount = (value: number) =>
  `OMR ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value)}`;

export default function AccountingSummaryCard({
  cash,
  bank,
  openingBalance,
  onConfirm,
}: AccountingSummaryCardProps) {
  return (
    <div className="w-full max-w-[560px]">
      {/* Balance cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        {/* Cash card */}
        <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-[0_8px_24px_rgba(26,94,249,0.06)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-[12px] bg-[#E8F5E9] flex items-center justify-center">
              <Banknote className="w-5 h-5 text-[#2E7D32]" />
            </div>
            <p className="text-sm font-medium text-[#7D8998]">Cash Balance</p>
          </div>
          <p className="text-2xl font-bold text-[#1A2B4A]">
            {formatAmount(cash)}
          </p>
        </div>

        {/* Bank card */}
        <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-[0_8px_24px_rgba(26,94,249,0.06)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-[12px] bg-[#E3F2FD] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1565C0]" />
            </div>
            <p className="text-sm font-medium text-[#7D8998]">Bank Balance</p>
          </div>
          <p className="text-2xl font-bold text-[#1A2B4A]">
            {formatAmount(bank)}
          </p>
        </div>
      </div>

      {/* Opening balance highlight */}
      <div className="rounded-[20px] border-2 border-[#BFD1FF] bg-gradient-to-r from-[#F0F4FF] to-[#F8FAFF] p-6 mb-5 shadow-[0_8px_24px_rgba(26,94,249,0.08)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-[14px] bg-[#1A5EF9] flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#52627C]">
                Total Opening Balance
              </p>
              <p className="text-xs text-[#8A9BB5]">Cash + Bank combined</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1A5EF9]">
            {formatAmount(openingBalance)}
          </p>
        </div>
      </div>

      {/* Confirm button */}
      <button
        type="button"
        onClick={onConfirm}
        className="h-12 w-full rounded-[14px] bg-[#1A5EF9] text-white font-semibold text-sm transition-all hover:bg-[#0E4FDE] hover:shadow-[0_8px_20px_rgba(26,94,249,0.3)] active:scale-[0.98]"
      >
        Confirm &amp; Start Session
      </button>
      <p className="text-center text-xs text-[#8A9BB5] mt-3">
        This will open today's accounting session with the balances shown above
      </p>
    </div>
  );
}
